import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search as SearchIcon, Stethoscope, BadgeCheck, FileText, Download, Radio, Send, Mail, MessageSquare, Bell, Loader2 } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { Pagination } from "@/components/Pagination";
import { FilterPills } from "@/components/FilterPills";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { downloadCSV } from "@/lib/csv";

const STATUS_OPTIONS = [
  { key: "all", label: "All" },
  { key: "verified", label: "Verified" },
  { key: "active", label: "Subscription active" },
  { key: "inactive", label: "Subscription inactive" },
  { key: "suspended", label: "Suspended" },
];

export default function AdminCollectors() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testScope, setTestScope] = useState("all");
  const [testChannels, setTestChannels] = useState({ email: true, sms: true, push: true });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [smsDelivery, setSmsDelivery] = useState(null);
  const navigate = useNavigate();

  const load = async (query = "", p = 1, st = status, rg = range) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: pageSize };
      if (query) params.q = query;
      if (st && st !== "all") params.status = st;
      if (rg.start) params.start = rg.start;
      if (rg.end) params.end = rg.end;
      const { data } = await api.get("/admin/collectors", { params });
      setItems(data.items);
      setTotal(data.total);
      setPage(data.page);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(q.trim(), 1, status, range); /* eslint-disable-next-line */ }, [status]);

  const applyRange = (start, end) => { const rg = { start, end }; setRange(rg); load(q.trim(), 1, status, rg); };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = { export: true };
      if (q.trim()) params.q = q.trim();
      if (status !== "all") params.status = status;
      if (range.start) params.start = range.start;
      if (range.end) params.end = range.end;
      const { data } = await api.get("/admin/collectors", { params });
      const rows = (data.items || []).map((c) => ({
        business: c.business_name || "",
        contact_name: [c.first_name, c.last_name].filter(Boolean).join(" "),
        email: c.email || "",
        phone: c.phone || "",
        city: c.city || "",
        state: c.state || "",
        zip: c.zip_code || "",
        verified: c.verified ? "Yes" : "No",
        subscription: c.subscription_active ? "Active" : "Inactive",
        suspended: c.suspended ? "Yes" : "No",
        rating: c.rating_avg || 0,
        reviews: c.rating_count || 0,
        documents: c.cert_count || 0,
      }));
      if (!rows.length) { toast.error("Nothing to export"); return; }
      downloadCSV(`collectors-${status}.csv`, rows);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setExporting(false);
    }
  };

  const toggleChannel = (k) => setTestChannels((c) => ({ ...c, [k]: !c[k] }));

  const pollBroadcastSms = async (ids, attempt = 0) => {
    try {
      const { data } = await api.post("/admin/collectors/broadcast-sms-status", { ids });
      const done = data.all_resolved || attempt >= 7;
      setSmsDelivery({ status: done ? "done" : "checking", counts: data.counts, total: data.total });
      if (done) return;
      setTimeout(() => pollBroadcastSms(ids, attempt + 1), 4000);
    } catch (e) {
      if (attempt >= 7) { setSmsDelivery((d) => (d ? { ...d, status: "done" } : null)); return; }
      setTimeout(() => pollBroadcastSms(ids, attempt + 1), 4000);
    }
  };

  const sendTest = async () => {
    const channels = Object.keys(testChannels).filter((k) => testChannels[k]);
    if (!channels.length) { toast.error("Select at least one channel to test."); return; }
    setTesting(true);
    setTestResult(null);
    setSmsDelivery(null);
    try {
      const { data } = await api.post("/admin/collectors/test-broadcast", { scope: testScope, channels });
      setTestResult(data);
      toast.success(`Test request sent to ${data.stats.collectors} collector(s).`);
      if (data.channels.includes("sms") && (data.sms_ids || []).length) {
        setSmsDelivery({ status: "checking", counts: null, total: data.sms_ids.length });
        pollBroadcastSms(data.sms_ids);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="container-x py-12" data-testid="admin-collectors">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <Stethoscope className="w-6 h-6 text-[#2D6A4F]" /> Collectors ({total})
          </h1>
          <p className="text-[#5C6B64] mt-2">Manage collector profiles, verification & subscription status, and review their uploaded documents.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-collectors-test-comms-toggle"
            onClick={() => setTestOpen((v) => !v)}
            className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-3"
          >
            <Radio className="w-4 h-4" /> Test communications
          </button>
          <button
            data-testid="admin-collectors-export-csv"
            onClick={exportCSV}
            disabled={exporting || (!loading && items.length === 0)}
            className="btn-secondary inline-flex items-center gap-2 text-sm py-2 px-3 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      {testOpen && (
        <div data-testid="admin-collectors-test-panel" className="mt-6 rounded-xl border border-[#2D6A4F]/25 bg-[#F4F8F5] p-5">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-[#2D6A4F]/12 flex items-center justify-center">
              <Send className="w-4 h-4 text-[#2D6A4F]" />
            </div>
            <div>
              <h2 className="font-heading text-lg text-[#1E3F33] font-semibold">Send a test customer request</h2>
              <p className="text-sm text-[#5C6B64] mt-1 max-w-2xl">
                Sends a clearly-labelled <strong>TEST</strong> lead notification to your collectors so you can confirm they actually
                receive communications. No real customer is involved and no leads are created.
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2 max-w-2xl">
            <div>
              <label className="text-xs font-medium text-[#1E3F33] uppercase tracking-wide">Send to</label>
              <div className="mt-2 flex flex-col gap-2">
                {[
                  { key: "all", label: "All registered collectors" },
                  { key: "active", label: "Active subscribers only" },
                ].map((o) => (
                  <label key={o.key} className="flex items-center gap-2 text-sm text-[#1E3F33] cursor-pointer" data-testid={`admin-collectors-test-scope-${o.key}`}>
                    <input type="radio" name="test-scope" checked={testScope === o.key} onChange={() => setTestScope(o.key)} className="accent-[#2D6A4F]" />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#1E3F33] uppercase tracking-wide">Channels</label>
              <div className="mt-2 flex flex-col gap-2">
                {[
                  { key: "email", label: "Email", Icon: Mail },
                  { key: "sms", label: "SMS text", Icon: MessageSquare },
                  { key: "push", label: "Push / in-app", Icon: Bell },
                ].map(({ key, label, Icon }) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-[#1E3F33] cursor-pointer" data-testid={`admin-collectors-test-channel-${key}`}>
                    <input type="checkbox" checked={!!testChannels[key]} onChange={() => toggleChannel(key)} className="accent-[#2D6A4F]" />
                    <Icon className="w-3.5 h-3.5 text-[#5C6B64]" /> {label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <button
            data-testid="admin-collectors-test-send"
            onClick={sendTest}
            disabled={testing}
            className="btn-primary mt-5 inline-flex items-center gap-2 text-sm py-2.5 px-4 disabled:opacity-60"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {testing ? "Sending…" : "Send test request"}
          </button>

          {testResult && (
            <div data-testid="admin-collectors-test-result" className="mt-5 rounded-lg border border-[#1E3F33]/10 bg-white p-4">
              <p className="text-sm text-[#1E3F33] font-medium">
                Sent to <strong>{testResult.stats.collectors}</strong> collector(s) — scope: {testResult.scope}
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                {testResult.channels.map((ch) => {
                  const s = testResult.stats[ch] || {};
                  return (
                    <div key={ch} className="rounded-lg bg-[#F4F8F5] px-3 py-2" data-testid={`admin-collectors-test-result-${ch}`}>
                      <div className="text-xs uppercase tracking-wide text-[#5C6B64]">{ch}</div>
                      <div className="text-sm text-[#1E3F33] mt-1">
                        <span className="text-[#2D6A4F] font-semibold">{s.sent || 0} sent</span>
                        {s.failed ? <span className="text-[#E63946]"> · {s.failed} failed</span> : null}
                        {s.no_contact ? <span className="text-[#8A9A86]"> · {s.no_contact} no {ch === "email" ? "email" : ch === "sms" ? "phone" : "device"}</span> : null}
                      </div>
                      {ch === "sms" && smsDelivery && (
                        <div className="mt-1.5 text-[11px] border-t border-[#1E3F33]/8 pt-1.5" data-testid="admin-collectors-test-sms-delivery">
                          <div className="uppercase tracking-wide text-[#8A9A86] mb-0.5">Carrier delivery</div>
                          {!smsDelivery.counts ? (
                            <span className="text-[#8A9A86]">Checking…</span>
                          ) : (
                            <span>
                              <span className="text-[#2D6A4F] font-semibold">{smsDelivery.counts.delivered} delivered</span>
                              {smsDelivery.counts.filtered ? <span className="text-[#CC5A3A]"> · {smsDelivery.counts.filtered} filtered</span> : null}
                              {smsDelivery.counts.failed ? <span className="text-[#E63946]"> · {smsDelivery.counts.failed} failed</span> : null}
                              {smsDelivery.counts.pending ? <span className="text-[#8A9A86]"> · {smsDelivery.counts.pending} pending</span> : null}
                              {smsDelivery.status === "checking" ? <span className="text-[#8A9A86]"> · checking…</span> : null}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-[#8A9A86] mt-3">
                "Sent" means the message was accepted by the notification provider. SMS delivery also depends on the recipient's carrier.
              </p>
            </div>
          )}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); load(q.trim(), 1, status); }} className="mt-6 flex items-center gap-2 max-w-md">
        <input data-testid="admin-collectors-search" className="input-soft" placeholder="Search business, name, email, city…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn-primary py-2.5 px-4 inline-flex items-center gap-2" data-testid="admin-collectors-search-btn"><SearchIcon className="w-4 h-4" /> Search</button>
      </form>

      <div className="mt-4 flex flex-col gap-3">
        <FilterPills options={STATUS_OPTIONS} value={status} onChange={setStatus} testidPrefix="admin-collectors-filter" />
        <DateRangeFilter onChange={applyRange} />
      </div>

      {loading ? (
        <div className="mt-10 text-[#5C6B64]">Loading…</div>
      ) : items.length === 0 ? (
        <p className="mt-8 text-sm text-[#8A9A86]">No collectors found.</p>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-xl border border-[#1E3F33]/10">
          <table className="w-full text-sm" data-testid="admin-collectors-table">
            <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
              <th className="px-4 py-2.5 font-medium">Business</th>
              <th className="px-4 py-2.5 font-medium">Location</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Docs</th>
              <th className="px-4 py-2.5 font-medium"></th>
            </tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-[#1E3F33]/8" data-testid={`admin-collector-row-${c.id}`}>
                  <td className="px-4 py-2.5"><div className="font-medium text-[#1E3F33]">{c.business_name || "—"}</div><div className="text-xs text-[#8A9A86]">{c.email || "—"}</div>{c.company_id && <div className="text-[10px] text-[#2D6A4F] mt-0.5" data-testid={`admin-collector-company-${c.id}`}>Company: {c.company_name}{c.has_login ? "" : " · no login yet"}</div>}</td>
                  <td className="px-4 py-2.5 text-[#5C6B64]">{[c.city, c.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-2.5 space-x-1 whitespace-nowrap">
                    {c.suspended && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E63946]/15 text-[#E63946]">Suspended</span>}
                    {c.verified && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#2D6A4F]/15 text-[#2D6A4F] inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" />Verified</span>}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.subscription_active ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : "bg-[#1E3F33]/10 text-[#5C6B64]"}`}>{c.subscription_active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="px-4 py-2.5 text-[#5C6B64]"><span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {c.cert_count}</span></td>
                  <td className="px-4 py-2.5 text-right"><button data-testid={`admin-collector-manage-${c.id}`} onClick={() => navigate(`/admin/collectors/${c.id}`)} className="btn-secondary text-xs py-1.5 px-3">Manage</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} total={total} pageSize={pageSize} onPage={(p) => load(q.trim(), p, status, range)} testid="admin-collectors-pagination" />
    </div>
  );
}
